import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Typography,
  Button,
  InputAdornment,
  Pagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { fetchFactures, deleteFacture } from '../redux/slices/facturesSlice';
import { AppDispatch, RootState } from '../redux/store';
import Layout from '../components/Layout';
import DrawerPanel from '../components/DrawerPanel';
import FactureForm from '../components/FactureForm';

const FacturesList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { factures, loading, pagination } = useSelector((state: RootState) => state.factures);

  const [search, setSearch] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchFactures({
      page,
      limit: 20,
      search,
      statut: statutFilter,
    }));
  }, [dispatch, page, search, statutFilter]);

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) {
      try {
        await dispatch(deleteFacture(id)).unwrap();
        dispatch(fetchFactures({ page, limit: 20, search, statut: statutFilter }));
      } catch (error) {
        console.error('Erreur suppression:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const getClientName = (facture: any) => {
    if (facture.type_client === 'entreprise') {
      return facture.raison_sociale;
    }
    return `${facture.client_nom} ${facture.client_prenom || ''}`.trim();
  };

  const getStatutColor = (statut: string) => {
    switch (statut) {
      case 'impaye':
        return 'error';
      case 'partiel':
        return 'warning';
      case 'paye':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(montant);
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h4" component="h1">
            Factures
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDrawerOpen(true)}
          >
            Nouvelle facture
          </Button>
        </Box>

        {/* Filtres */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Rechercher par numéro, client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Statut"
              value={statutFilter}
              onChange={(e) => setStatutFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Tous</MenuItem>
              <MenuItem value="impaye">Impayé</MenuItem>
              <MenuItem value="partiel">Partiellement payé</MenuItem>
              <MenuItem value="paye">Payé</MenuItem>
            </TextField>
          </Box>
        </Paper>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>N° Facture</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Montant total</TableCell>
                <TableCell align="right">Payé</TableCell>
                <TableCell align="right">Restant</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : !factures || factures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      Aucune facture trouvée
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                (factures || []).map((facture: any) => (
                  <TableRow
                    key={facture.id}
                    sx={{
                      '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {facture.numero_facture}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/clients/${facture.client_id}`}
                        style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 500 }}
                      >
                        {getClientName(facture)}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {new Date(facture.date_facture).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatMontant(facture.montant_total)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="success.main">
                        {formatMontant(facture.montant_paye)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={facture.montant_restant > 0 ? 'error' : 'text.secondary'}
                      >
                        {formatMontant(facture.montant_restant)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          facture.statut_paiement === 'impaye'
                            ? 'Impayé'
                            : facture.statut_paiement === 'partiel'
                            ? 'Partiel'
                            : 'Payé'
                        }
                        size="small"
                        color={getStatutColor(facture.statut_paiement)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <IconButton
                          component={Link}
                          to={`/factures/${facture.id}`}
                          size="small"
                          color="primary"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(facture.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}

        {/* Panel latéral pour nouvelle facture */}
        <DrawerPanel
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title="Nouvelle facture"
          width={700}
        >
          <FactureForm
            onSuccess={() => {
              setDrawerOpen(false);
              dispatch(fetchFactures({ page, limit: 20, search, statut: statutFilter }));
            }}
            onCancel={() => setDrawerOpen(false)}
          />
        </DrawerPanel>
      </Box>
    </Layout>
  );
};

export default FacturesList;
